/*
 * useOwnership — the prototype's ARCH-3 OWN-FRAMEWORK ported onto the app scope
 * (`redesign/optimal-hr-prototype-v6.html` L68892–68981).
 *
 * The prototype resolves three ownership-context classes from
 * `STATE.company` + `STATE.activeScope`:
 *
 *   - `ownUnitCtx()`        — unit-owned operations: Direct Workforce / each Project.
 *   - `ownCompanyRootCtx()` — company-owned config / governance at the company root.
 *   - `ownProviderCtx()`    — provider-commercial class (Billing): provider company root only.
 *
 * plus the central refusal copy `OWN_REFUSAL_COPY` and `ownRefuse(cls, label)`.
 *
 * The app scope (`auth.activeScope`) maps onto the prototype like this:
 *
 *   store scope                         proto STATE                          scopeKind
 *   ─────────────────────────────────   ──────────────────────────────────   ─────────
 *   null / { scope:'global' }           company=null,  kind 'all'            root
 *   { scope:'employer', COMPANY }       company=<id>,  kind 'all'            root
 *   { scope:'direct', employerId }      company=<id>,  kind 'direct'         direct
 *   { scope:'employer', OS_CLIENT|VEND} kind 'client'                        client
 *   { scope:'employer', OS_PROJECT }    kind 'project'                       project
 *
 * Owning scopes (direct / project) process; every other scope is read-only
 * oversight. The pure resolvers below are exported for unit parity with the
 * prototype; `useOwnership()` is the reactive wrapper modules consume.
 */
import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';

import { useAuthStore, type ActiveScope } from '@/stores/auth';
import { employerFullName, type Employer } from '@/api/services/employers';
import { useScopeTree } from '@/composables/useScopeTree';

/* ── Public contract (other slices code against this) ──────────────── */

/** The resolved ownership context of the active scope. */
export interface OwnershipCtx {
  /** `owning` at Direct Workforce / Project; `oversight` everywhere else. */
  mode: 'owning' | 'oversight';
  /** The prototype scope kind (`all` → `root`). */
  scopeKind: 'root' | 'direct' | 'client' | 'project';
  /** The owning unit key (`'DIRECT'` or the project's employer id); `null` in oversight. */
  unitId: string | null;
  /** Human label of the scope (localised). */
  unitLabel: string;
  /** `true` only at an owning scope — the unit-class operations may run. */
  canProcess: boolean;
}

/** The four refusal classes of `OWN_REFUSAL_COPY` (proto L68922–68927). */
export type OwnRefusalClass = 'unit' | 'company' | 'governance' | 'commercial';

/* ── Pure prototype-parity resolvers ───────────────────────────────── */

/** The unit key the prototype uses for Direct Workforce (`ownUnitKeyOfStamp`). */
export const DIRECT_UNIT_KEY = 'DIRECT';

/** Proto `ownUnitCtx()` result. */
export interface OwnUnitCtx {
  key: string;
  kind: 'direct' | 'project';
  projectId: string | null;
  label: string;
}

/** Proto `ownCompanyRootCtx()` result. */
export interface OwnCompanyRootCtx {
  key: string;
  kind: 'companyRoot';
  label: string;
}

/** Proto `ownProviderCtx()` result. */
export interface OwnProviderCtx {
  key: string;
  kind: 'provider';
  label: string;
}

/**
 * The prototype's scope state, flattened: the selected company (proto
 * `STATE.company`) and the scope kind (proto `STATE.activeScope.kind`).
 */
export interface OwnScopeState {
  /** The company in context (`null` under All Scopes). */
  company: { id: string; name: string; isProvider: boolean } | null;
  kind: 'all' | 'direct' | 'client' | 'project';
  /** The client / vendor in scope (client + project kinds). */
  clientId?: string | null;
  /** The project in scope (project kind). */
  projectId?: string | null;
  /** The project's display name (project kind). */
  projectName?: string | null;
}

/** Label builders — default to the prototype's English; `useOwnership` localises them. */
export interface OwnLabels {
  directWorkforce: string;
  companyRoot: (name: string) => string;
  commercialOwner: (name: string) => string;
}

/** The prototype's own English labels (L68903, L68911, L68916). */
export const PROTO_OWN_LABELS: OwnLabels = {
  directWorkforce: 'Direct Workforce',
  companyRoot: (name) => `${name} · company root`,
  commercialOwner: (name) => `${name} · Commercial Owner`,
};

/**
 * Proto `ownUnitCtx()` (L68901): the owning unit — Direct Workforce or a
 * Project — or `null` in oversight.
 *
 * @param s      - The flattened scope state.
 * @param labels - Label builders (defaults to the prototype's English).
 * @returns The unit context, or `null`.
 */
export function ownUnitCtx(
  s: OwnScopeState,
  labels: OwnLabels = PROTO_OWN_LABELS,
): OwnUnitCtx | null {
  if (!s.company?.id) return null;
  if (s.kind === 'direct') {
    return { key: DIRECT_UNIT_KEY, kind: 'direct', projectId: null, label: labels.directWorkforce };
  }
  if (s.kind === 'project' && s.projectId) {
    return {
      key: s.projectId,
      kind: 'project',
      projectId: s.projectId,
      label: s.projectName || s.projectId,
    };
  }
  return null;
}

/**
 * Proto `ownCompanyRootCtx()` (L68908): the company root, only while a company
 * is selected on the `all` kind.
 *
 * @param s      - The flattened scope state.
 * @param labels - Label builders (defaults to the prototype's English).
 * @returns The company-root context, or `null`.
 */
export function ownCompanyRootCtx(
  s: OwnScopeState,
  labels: OwnLabels = PROTO_OWN_LABELS,
): OwnCompanyRootCtx | null {
  if (!s.company?.id) return null;
  if (s.kind !== 'all') return null;
  return {
    key: s.company.id,
    kind: 'companyRoot',
    label: labels.companyRoot(s.company.name || s.company.id),
  };
}

/**
 * Proto `ownProviderCtx()` (L68913): the commercial owner — the company root of
 * an outsourcing-provider company only.
 *
 * @param s      - The flattened scope state.
 * @param labels - Label builders (defaults to the prototype's English).
 * @returns The provider context, or `null`.
 */
export function ownProviderCtx(
  s: OwnScopeState,
  labels: OwnLabels = PROTO_OWN_LABELS,
): OwnProviderCtx | null {
  const root = ownCompanyRootCtx(s, labels);
  if (!root || !s.company?.isProvider) return null;
  return {
    key: s.company.id,
    kind: 'provider',
    label: labels.commercialOwner(s.company.name || s.company.id),
  };
}

/**
 * Proto `OWN_REFUSAL_COPY` (L68922–68927), verbatim — including the
 * prototype's own grammar on `governance`.
 */
export const OWN_REFUSAL_COPY: Record<OwnRefusalClass, (label: string) => string> = {
  unit: (l) =>
    `${l} happen at the owning scope (Direct Workforce / Project) — oversight is read-only review.`,
  company: (l) =>
    `${l} are company-owned reference data — manage them at the company context (company root).`,
  governance: (l) =>
    `${l} is company-level governance — manage it at the company context (company root).`,
  commercial: (l) =>
    `${l} belong to the commercial owner at the provider company root — this scope is a read-only commercial view.`,
};

/**
 * Proto `ownRefuse(cls, label)` text (L68928): unknown classes fall back to
 * `unit`, a blank label to "These operations".
 *
 * @param cls   - The refusal class (unknown → `unit`).
 * @param label - The operation label (blank → "These operations").
 * @returns The refusal sentence.
 */
export function ownRefusalText(cls: string | null | undefined, label?: string | null): string {
  const f = OWN_REFUSAL_COPY[(cls ?? '') as OwnRefusalClass] ?? OWN_REFUSAL_COPY.unit;
  return f(label || 'These operations');
}

/** `true` when `cls` is one of the four refusal classes. */
function isRefusalClass(cls: string): cls is OwnRefusalClass {
  return Object.prototype.hasOwnProperty.call(OWN_REFUSAL_COPY, cls);
}

/**
 * Flatten the app's store scope + resolved employer list onto the prototype's
 * scope state.
 *
 * @param scope              - The store's active scope (`null` → All Scopes).
 * @param employers          - The resolved employer list (may be empty while loading).
 * @param tenantEmployerId   - The tenant's `TENANT_EMPLOYER` id, when resolved.
 * @param tenantIsProvider   - The tenant's outsourcing-provider flag.
 * @returns The flattened prototype scope state.
 */
export function toOwnScopeState(
  scope: ActiveScope | null | undefined,
  employers: readonly Employer[],
  tenantEmployerId: string | null,
  tenantIsProvider: boolean,
): OwnScopeState {
  if (!scope || scope.scope === 'global') return { company: null, kind: 'all' };
  const find = (id: string | null | undefined): Employer | null =>
    id ? (employers.find((e) => e.id === id) ?? null) : null;
  const companyOf = (id: string) => {
    const e = find(id);
    return {
      id,
      name: e ? employerFullName(e) : id,
      // The provider company is the tenant's own company on a provider tenant
      // (proto: only `CMP-OPTIMALP` carries `companyType:'Outsourcing Provider'`).
      isProvider: tenantIsProvider && tenantEmployerId !== null && id === tenantEmployerId,
    };
  };
  if (scope.scope === 'direct') return { company: companyOf(scope.employerId), kind: 'direct' };

  const sel = find(scope.employerId);
  const kind = scope.kind ?? sel?.kind ?? 'COMPANY';
  if (kind === 'OS_PROJECT') {
    const clientId = sel?.parentId ?? null;
    const client = find(clientId);
    return {
      company: companyOf(client?.parentId ?? tenantEmployerId ?? scope.employerId),
      kind: 'project',
      clientId,
      projectId: scope.employerId,
      projectName: sel ? employerFullName(sel) : null,
    };
  }
  if (kind === 'OS_CLIENT' || kind === 'OS_VENDOR') {
    return {
      company: companyOf(sel?.parentId ?? tenantEmployerId ?? scope.employerId),
      kind: 'client',
      clientId: scope.employerId,
    };
  }
  // COMPANY (and an unknown kind — fail safe to read-only oversight).
  return { company: companyOf(scope.employerId), kind: 'all' };
}

/**
 * Derive the public {@link OwnershipCtx} from the flattened scope state.
 *
 * @param s          - The flattened scope state.
 * @param scopeLabel - The localised label of the scope (company / client / "All Scopes").
 * @param labels     - Label builders (defaults to the prototype's English).
 * @returns The ownership context.
 */
export function deriveOwnershipCtx(
  s: OwnScopeState,
  scopeLabel: string,
  labels: OwnLabels = PROTO_OWN_LABELS,
): OwnershipCtx {
  const unit = ownUnitCtx(s, labels);
  const scopeKind: OwnershipCtx['scopeKind'] = s.kind === 'all' ? 'root' : s.kind;
  return {
    mode: unit ? 'owning' : 'oversight',
    scopeKind,
    unitId: unit ? unit.key : null,
    unitLabel: unit ? unit.label : scopeLabel,
    canProcess: unit !== null,
  };
}

/* ── Reactive wrapper ──────────────────────────────────────────────── */

/** What {@link useOwnership} returns. */
export interface UseOwnership {
  /** The ownership context of the active scope. */
  ctx: ComputedRef<OwnershipCtx>;
  /**
   * The refusal copy for `action` when the active scope may NOT run it, else
   * `null`. `cls` picks the ownership class (default `unit`): `unit` needs an
   * owning scope, `company` / `governance` the company root, `commercial` the
   * provider company root.
   */
  refusal(action: string, cls?: OwnRefusalClass): string | null;
  /** Proto `ownUnitCtx()` over the active scope. */
  unitCtx: ComputedRef<OwnUnitCtx | null>;
  /** Proto `ownCompanyRootCtx()` over the active scope. */
  companyRootCtx: ComputedRef<OwnCompanyRootCtx | null>;
  /** Proto `ownProviderCtx()` over the active scope. */
  providerCtx: ComputedRef<OwnProviderCtx | null>;
}

const EMPTY_EMPLOYERS: Employer[] = [];

/**
 * The cached employer list, acquired defensively: specs that mount a page
 * without a `VueQueryPlugin` get an empty list instead of a crash.
 *
 * @returns A reactive ref of the resolved employer list.
 */
function resolveEmployers(): Ref<Employer[]> {
  try {
    return useScopeTree().employers;
  } catch {
    return ref(EMPTY_EMPLOYERS);
  }
}

/**
 * The ownership framework over the active app scope.
 *
 * @returns The reactive ownership context + the refusal helper.
 */
export function useOwnership(): UseOwnership {
  const auth = useAuthStore();
  const { activeScope, tenantEmployerId, isProvider } = storeToRefs(auth);
  const { t } = useI18n();
  const employers = resolveEmployers();

  const labels = computed<OwnLabels>(() => ({
    directWorkforce: t('v2.own.direct-workforce'),
    companyRoot: (name) => t('v2.own.company-root', { name }),
    commercialOwner: (name) => t('v2.own.commercial-owner', { name }),
  }));

  const state = computed<OwnScopeState>(() =>
    toOwnScopeState(activeScope.value, employers.value, tenantEmployerId.value, isProvider.value),
  );

  const scopeLabel = computed<string>(() => {
    const s = state.value;
    if (s.kind === 'client' && s.clientId) {
      const c = employers.value.find((e) => e.id === s.clientId);
      return c ? employerFullName(c) : s.clientId;
    }
    return s.company ? s.company.name : t('v2.scope.all-scopes');
  });

  const unitCtx = computed(() => ownUnitCtx(state.value, labels.value));
  const companyRootCtx = computed(() => ownCompanyRootCtx(state.value, labels.value));
  const providerCtx = computed(() => ownProviderCtx(state.value, labels.value));
  const ctx = computed<OwnershipCtx>(() =>
    deriveOwnershipCtx(state.value, scopeLabel.value, labels.value),
  );

  function refusal(action: string, cls: OwnRefusalClass = 'unit'): string | null {
    const c: OwnRefusalClass = isRefusalClass(cls) ? cls : 'unit';
    const allowed =
      c === 'unit'
        ? unitCtx.value !== null
        : c === 'commercial'
          ? providerCtx.value !== null
          : companyRootCtx.value !== null;
    if (allowed) return null;
    return t(`v2.own.refusal.${c}`, { label: action || t('v2.own.these-operations') });
  }

  return { ctx, refusal, unitCtx, companyRootCtx, providerCtx };
}
